import { collection, getDocs, addDoc, deleteDoc, doc, query, where, orderBy, Timestamp, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { auth } from '../firebase/firebase';

const schedulesCollectionRef = collection(db, 'schedules');

export const getScheduledEmails = async () => {
  try {
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    // Create a query against the collection to only get this user's scheduled emails
    const q = query(
      schedulesCollectionRef, 
      where("userId", "==", userId),
      orderBy("scheduledFor", "asc")
    );
    
    try {
      const querySnapshot = await getDocs(q);
      
      // If we get here, the query succeeded even if the collection is empty
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          campaignId: data.campaignId,
          campaignTitle: data.campaignTitle,
          recipients: data.recipients || [],
          scheduledFor: data.scheduledFor?.toDate ? data.scheduledFor.toDate() : data.scheduledFor,
          status: data.status || 'pending',
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          userId: data.userId
        };
      });
    } catch (queryError) {
      // If there's a query error, it might be because the collection doesn't exist yet
      console.warn("Query error (collection might not exist yet):", queryError);
      return []; // Return empty array instead of failing
    }
  } catch (error) {
    console.error("Error getting scheduled emails: ", error);
    throw error;
  }
};

export const scheduleEmail = async (scheduleData) => {
  try {
    // Make sure we have the current user and they're authenticated
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      console.error("No authenticated user found when trying to schedule email");
      throw new Error('User not authenticated');
    }
    
    const userId = currentUser.uid;
    console.log("Scheduling email with authenticated user ID:", userId);
    
    // Convert Date to Firestore Timestamp for proper storage
    const firestoreData = {
      ...scheduleData,
      userId, // Ensure userId is included
      createdAt: new Date(),
      scheduledFor: scheduleData.scheduledFor instanceof Date ? 
        Timestamp.fromDate(scheduleData.scheduledFor) : 
        scheduleData.scheduledFor,
      status: 'pending'
    };
    
    // Log the data being sent (minus any sensitive info)
    console.log("Scheduling with data:", {
      campaignId: scheduleData.campaignId,
      userId,
      recipientCount: scheduleData.recipients?.length || 0,
      scheduledFor: firestoreData.scheduledFor
    });
    
    const docRef = await addDoc(schedulesCollectionRef, firestoreData);
    console.log("Successfully created schedule with ID:", docRef.id);
    return docRef;
  } catch (error) {
    console.error("Error scheduling email: ", error);
    throw error;
  }
};

export const deleteScheduledEmail = async (id) => {
  try {
    const scheduleDoc = doc(db, 'schedules', id);
    return await deleteDoc(scheduleDoc);
  } catch (error) {
    console.error("Error deleting scheduled email: ", error);
    throw error;
  }
};

/**
 * Get campaigns that are scheduled to be sent now or in the past
 * @returns {Promise<Array>} - Array of due campaigns
 */
export const getDueCampaigns = async () => {
  try {
    const now = Timestamp.fromDate(new Date());
    
    // Create a query for campaigns that should be sent now
    const q = query(
      schedulesCollectionRef,
      where("status", "==", "pending"),
      where("scheduledFor", "<=", now),
      orderBy("scheduledFor", "asc")
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        campaignId: data.campaignId,
        campaignTitle: data.campaignTitle,
        recipients: data.recipients || [],
        scheduledFor: data.scheduledFor?.toDate ? data.scheduledFor.toDate() : data.scheduledFor,
        status: data.status || 'pending',
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
        userId: data.userId
      };
    });
  } catch (error) {
    console.error("Error getting due campaigns: ", error);
    throw error;
  }
};

/**
 * Get campaign details by ID
 * @param {string} campaignId - The campaign ID
 * @returns {Promise<Object>} - Campaign data
 */
export const getCampaignById = async (campaignId) => {
  try {
    const campaignDoc = doc(db, 'campaigns', campaignId);
    const docSnap = await getDoc(campaignDoc);
    
    if (!docSnap.exists()) {
      throw new Error(`Campaign with ID ${campaignId} not found`);
    }
    
    const data = docSnap.data();
    return {
      id: campaignId,
      title: data.title || '',
      subject: data.subject || '',
      body: data.body || '',
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
      userId: data.userId
    };
  } catch (error) {
    console.error(`Error getting campaign by ID ${campaignId}:`, error);
    throw error;
  }
};

/**
 * Update the status of a scheduled campaign
 * @param {string} scheduleId - The schedule ID
 * @param {string} status - New status (processing, sent, failed)
 * @param {Object} resultsData - Results data to store (optional)
 * @returns {Promise<void>}
 */
export const updateScheduleStatus = async (scheduleId, status, resultsData = null) => {
  try {
    const scheduleRef = doc(db, 'schedules', scheduleId);
    
    const updateData = { 
      status, 
      processedAt: Timestamp.fromDate(new Date())
    };
    
    if (resultsData) {
      updateData.results = resultsData;
    }
    
    if (status === 'failed' && resultsData?.error) {
      updateData.error = resultsData.error;
    }
    
    await updateDoc(scheduleRef, updateData);
  } catch (error) {
    console.error(`Error updating schedule status for ${scheduleId}:`, error);
    throw error;
  }
};
